//
import type { PackingSlipStoreState, PackingSlipState } from '@/types/Store'
import { defineStore } from 'pinia'
import { STAFF } from '@/const/AccountTypes'
import { SINGLE } from '@/const/AssignmentTypes'
import { CREATED } from '@/const/PackingSlipEventTypes'
import { none } from '@/services/Helpers'
import AccountProxy from '@/proxies/AccountProxy'
import UserTransformer from '@/transformers/UserTransformer'
import PackingSlipProxy from '@/proxies/PackingSlipProxy'

const defaultPackingSlip = (): PackingSlipState => ({
  downloadUrl: null,
  economyPreview: null,
  id: null,
  priorityPreview: null,
  state: null,
})

export const usePackingSlipStore = defineStore('packingSlip', {
  state: (): PackingSlipStoreState => ({
    assignmentType: SINGLE,
    packingSlip: defaultPackingSlip(),
    checkedItems: [],
    checkedAssignments: [],
    packingSlips: [],
    total: 0,
    totalWeight: 0,
    query: null,
    companyId: null,
    staffs: [],
  }),
  actions: {
    setPackingSlip(packingSlip: PackingSlipState | null) {
      this.packingSlip = packingSlip ?? defaultPackingSlip()
    },
    setAssignmentType(assignmentType: string) {
      this.assignmentType = assignmentType
    },
    setCompanyId(companyId: string | null) {
      this.companyId = companyId
    },
    resetPackingSlip() {
      this.setPackingSlip(defaultPackingSlip())
    },
    updateCheckedItems(checkedItems: string[]) {
      this.checkedItems = checkedItems
    },
    clearCheckedItems() {
      this.checkedItems = []
    },
    updateCheckedAssignments(checkedAssignments: string[]) {
      this.checkedAssignments = checkedAssignments
    },
    create(data: any) {
      this.setPackingSlip({ ...defaultPackingSlip(), state: CREATED })

      return new PackingSlipProxy()
        .create({ ...data, assignmentType: SINGLE })
        .then((response) => {
          this.setPackingSlip({
            downloadUrl: response.data.downloadUrl,
            economyPreview: response.data.economyPreview,
            id: response.data.id,
            priorityPreview: response.data.priorityPreview,
            state: CREATED,
          })
          return response
        })
        .catch(none)
    },
    getPackingSlips(data: Record<string, any> = {}) {
      return new PackingSlipProxy({ ...data, assignmentType: SINGLE })
        .all()
        .then((response) => {
          if (response?.data) {
            this.packingSlips = response.data
            this.total = response.total
            this.totalWeight = response.totalWeight
          }
          return response
        })
        .catch(none)
    },
    deletePackingSlip(id: string) {
      return new PackingSlipProxy().destroy(id).then(() => {
        this.setPackingSlip(defaultPackingSlip())
      })
    },
    getPackingSlip(id: string) {
      return new PackingSlipProxy()
        .find(id)
        .then((response) => {
          if (response?.data) {
            this.setPackingSlip(response.data)
          }
          return response
        })
        .catch(none)
    },
    removeItems({ id, items }: { id: string; items: string[] }) {
      return new PackingSlipProxy()
        .removeItems(id, items)
        .then(() => {
          this.updateCheckedItems([])
        })
        .catch(none)
    },
    fetchStaffs() {
      return new AccountProxy({ type: STAFF })
        .all()
        .then((response) => {
          if (response?.data) {
            this.staffs = UserTransformer.fetchCollection(response.data)
          }
          return response
        })
        .catch(none)
    },
  },
})

export type PackingSlipStore = ReturnType<typeof usePackingSlipStore>
